/* PS · Display-only currency conversion
   ------------------------------------------------------------------
   The store sells through Razorpay / PhonePe, not Shopify Payments, so
   Shopify can only ever present and charge INR — `enabledPresentmentCurrencies`
   is ["INR"]. This converts the *displayed* figures so an overseas shopper can
   read the price in their own money. Checkout still charges INR, which is why
   every converted price carries a note saying so. Nothing here touches the
   values Shopify uses for the cart, the totals or the order.

   Rates come from the free @fawazahmed0/currency-api (CDN-hosted, no key) and
   are cached for twelve hours. If they can't be fetched the picker never
   appears and the whole site stays in rupees — a wrong price is worse than no
   choice of currency.  — Arnav, 3 Oct */
(function () {
  'use strict';

  var STORE = 'INR';
  var KEY_RATES = 'ps_fx_rates';
  var KEY_PICK = 'ps_fx_currency';
  var TTL = 12 * 60 * 60 * 1000;

  /* Display-only, so the list isn't limited to what the gateway can settle.
     Intl does the symbol, grouping and decimal rules for each one. */
  var CURRENCIES = [
    ['INR', 'Indian Rupee'], ['USD', 'US Dollar'], ['EUR', 'Euro'],
    ['GBP', 'British Pound'], ['AED', 'UAE Dirham'], ['AUD', 'Australian Dollar'],
    ['CAD', 'Canadian Dollar'], ['SGD', 'Singapore Dollar'], ['CHF', 'Swiss Franc'],
    ['JPY', 'Japanese Yen'], ['CNY', 'Chinese Yuan'], ['HKD', 'Hong Kong Dollar'],
    ['NZD', 'New Zealand Dollar'], ['SEK', 'Swedish Krona'], ['NOK', 'Norwegian Krone'],
    ['DKK', 'Danish Krone'], ['ZAR', 'South African Rand'], ['SAR', 'Saudi Riyal'],
    ['QAR', 'Qatari Riyal'], ['KWD', 'Kuwaiti Dinar'], ['BHD', 'Bahraini Dinar'],
    ['OMR', 'Omani Rial'], ['JOD', 'Jordanian Dinar'], ['ILS', 'Israeli Shekel'],
    ['TRY', 'Turkish Lira'], ['RUB', 'Russian Ruble'], ['PLN', 'Polish Zloty'],
    ['CZK', 'Czech Koruna'], ['HUF', 'Hungarian Forint'], ['RON', 'Romanian Leu'],
    ['MYR', 'Malaysian Ringgit'], ['THB', 'Thai Baht'], ['IDR', 'Indonesian Rupiah'],
    ['PHP', 'Philippine Peso'], ['VND', 'Vietnamese Dong'], ['KRW', 'South Korean Won'],
    ['TWD', 'Taiwan Dollar'], ['LKR', 'Sri Lankan Rupee'], ['NPR', 'Nepalese Rupee'],
    ['BDT', 'Bangladeshi Taka'], ['PKR', 'Pakistani Rupee'], ['MVR', 'Maldivian Rufiyaa'],
    ['BRL', 'Brazilian Real'], ['MXN', 'Mexican Peso'], ['ARS', 'Argentine Peso'],
    ['CLP', 'Chilean Peso'], ['COP', 'Colombian Peso'], ['PEN', 'Peruvian Sol'],
    ['EGP', 'Egyptian Pound'], ['NGN', 'Nigerian Naira'], ['KES', 'Kenyan Shilling'],
    ['GHS', 'Ghanaian Cedi'], ['TZS', 'Tanzanian Shilling'], ['MAD', 'Moroccan Dirham'],
    ['MUR', 'Mauritian Rupee'], ['FJD', 'Fijian Dollar']
  ];

  /* Elements whose text is a price. Everything else on the page is left alone —
     a blanket sweep for rupee-shaped strings would catch size charts, blog copy
     and anything else with a number in it. */
  var ROOTS = [
    '[data-ps-price]', '[data-ps-price-current]',
    '.ps-card__compare', '.ps-pdp__compare',
    '.price', '.price-item',
    '.totals__subtotal-value', '.totals__shipping-value',
    '.cart-item__price-wrapper', '.cart-item__prices',
    '[data-ps-money]'
  ].join(',');

  /* Shopify renders INR as ₹1,234.00; Rs. and the bare code show up in a few
     older strings, so all three are matched. */
  var MONEY = /(?:₹|Rs\.?|INR)\s*([\d,]+(?:\.\d{1,2})?)/g;

  var orig = new WeakMap();
  var rates = null;
  var current = STORE;
  var observer = null;
  var writing = false;

  function store(k, v) { try { localStorage.setItem(k, v); } catch (e) {} }
  function read(k) { try { return localStorage.getItem(k); } catch (e) { return null; } }

  function fetchRates() {
    var cached = read(KEY_RATES);
    if (cached) {
      try {
        var c = JSON.parse(cached);
        if (c && c.at && Date.now() - c.at < TTL && c.r) return Promise.resolve(c.r);
      } catch (e) {}
    }
    var urls = [
      'https://cdn.jsdelivr.net/npm/@fawazahmed0/currency-api@latest/v1/currencies/inr.json',
      'https://latest.currency-api.pages.dev/v1/currencies/inr.json'
    ];
    function tryAt(i) {
      if (i >= urls.length) return Promise.reject();
      return fetch(urls[i])
        .then(function (r) { if (!r.ok) throw 0; return r.json(); })
        .then(function (j) {
          var r = j && j.inr;
          if (!r || !r.usd) throw 0;
          store(KEY_RATES, JSON.stringify({ at: Date.now(), r: r }));
          return r;
        })
        .catch(function () { return tryAt(i + 1); });
    }
    return tryAt(0);
  }

  function fmt(amount, code) {
    try {
      return new Intl.NumberFormat('en', {
        style: 'currency', currency: code,
        minimumFractionDigits: 0, maximumFractionDigits: 0
      }).format(amount);
    } catch (e) {
      return code + ' ' + Math.round(amount).toLocaleString('en');
    }
  }

  function convertText(text, code) {
    MONEY.lastIndex = 0;
    return text.replace(MONEY, function (match, num) {
      var inr = parseFloat(num.replace(/,/g, ''));
      if (!isFinite(inr)) return match;
      var rate = rates[code.toLowerCase()];
      if (!rate) return match;
      return fmt(inr * rate, code);
    });
  }

  function apply() {
    var code = current;
    writing = true;
    document.querySelectorAll(ROOTS).forEach(function (root) {
      var walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, null);
      var nodes = [], n;
      while ((n = walker.nextNode())) nodes.push(n);
      nodes.forEach(function (node) {
        /* Always convert from the rupee original, never from an already
           converted string — otherwise switching currency compounds. */
        var source = orig.has(node) ? orig.get(node) : node.nodeValue;
        if (!/[₹]|Rs\.?|INR/.test(source)) return;
        if (!orig.has(node)) orig.set(node, source);
        var next = code === STORE ? source : convertText(source, code);
        if (node.nodeValue !== next) node.nodeValue = next;
      });
    });
    note();
    writing = false;
  }

  /* The shopper is reading one number and will be charged another, so say so
     wherever a converted price appears rather than burying it in the footer. */
  function note() {
    var existing = document.querySelectorAll('[data-ps-fx-note]');
    if (current === STORE) {
      existing.forEach(function (el) { el.remove(); });
      return;
    }
    var spots = document.querySelectorAll('[data-ps-price-current], .totals__subtotal-value');
    spots.forEach(function (spot) {
      var holder = spot.parentNode;
      if (!holder || holder.querySelector('[data-ps-fx-note]')) return;
      var el = document.createElement('span');
      el.setAttribute('data-ps-fx-note', '');
      el.className = 'ps-fx-note ps-body-12t';
      el.textContent = 'Approx. in ' + current + ' — charged in INR at checkout.';
      holder.appendChild(el);
    });
    document.querySelectorAll('[data-ps-fx-note]').forEach(function (el) {
      el.textContent = 'Approx. in ' + current + ' — charged in INR at checkout.';
    });
  }

  function label() {
    document.querySelectorAll('[data-ps-currency-label]').forEach(function (el) {
      el.textContent = current;
    });
    document.querySelectorAll('[data-ps-currency-select]').forEach(function (el) {
      if (el.value !== current) el.value = current;
    });
  }

  function pick(code) {
    current = code;
    store(KEY_PICK, code);
    label();
    apply();
  }

  function buildPickers() {
    document.querySelectorAll('[data-ps-currency-select]').forEach(function (sel) {
      if (sel.dataset.psBuilt) return;
      sel.dataset.psBuilt = '1';
      CURRENCIES.forEach(function (c) {
        if (c[0] !== STORE && !rates[c[0].toLowerCase()]) return;
        var o = document.createElement('option');
        o.value = c[0];
        o.textContent = c[1] + ' (' + c[0] + ')';
        sel.appendChild(o);
      });
      sel.addEventListener('change', function () { pick(sel.value); });
    });
    document.querySelectorAll('[data-ps-currency]').forEach(function (el) {
      el.hidden = false;
    });
  }

  function watch() {
    if (!('MutationObserver' in window)) return;
    var pending = null;
    observer = new MutationObserver(function () {
      if (writing) return;
      clearTimeout(pending);
      /* Cart drawer, variant switches and filter re-renders all drop fresh
         rupee markup back into the page; re-run once they settle. */
      pending = setTimeout(function () { if (current !== STORE) apply(); }, 60);
    });
    observer.observe(document.body, { childList: true, subtree: true, characterData: true });
  }

  
  /* ---- where is the shopper? ------------------------------------------------
     Shopify resolves the visitor's country from their IP and serves it at
     /browsing_context_suggestions.json — the same endpoint its own Geolocation
     app uses. Same-origin, no third-party service, nothing to sign up for.
     Cached for the session so it costs one request, not one per page. */
  var KEY_GEO = 'ps_fx_country';

  function detectCountry() {
    var cached = read(KEY_GEO);
    if (cached) return Promise.resolve(cached);
    return fetch('/browsing_context_suggestions.json', { credentials: 'same-origin' })
      .then(function (r) { if (!r.ok) throw 0; return r.json(); })
      .then(function (j) {
        var cc = j && j.detected_values && j.detected_values.country && j.detected_values.country.handle;
        if (!cc) throw 0;
        cc = String(cc).toUpperCase();
        store(KEY_GEO, cc);
        return cc;
      })
      .catch(function () { return null; });
  }

  /* Country to the currency we show there. Everywhere unlisted falls to USD,
     matching the catch-all you picked for the international market. */
  var EURO = 'AT BE HR CY EE FI FR DE GR IE IT LV LT LU MT NL PT SK SI ES AD MC SM VA ME XK'.split(' ');
  var BY_COUNTRY = {
    IN: 'INR', US: 'USD', GB: 'GBP', AE: 'AED', AU: 'AUD', CA: 'CAD', SG: 'SGD',
    CH: 'CHF', LI: 'CHF', JP: 'JPY', CN: 'CNY', HK: 'HKD', NZ: 'NZD', SE: 'SEK',
    NO: 'NOK', DK: 'DKK', ZA: 'ZAR', SA: 'SAR', QA: 'QAR', KW: 'KWD', BH: 'BHD',
    OM: 'OMR', JO: 'JOD', IL: 'ILS', TR: 'TRY', RU: 'RUB', PL: 'PLN', CZ: 'CZK',
    HU: 'HUF', RO: 'RON', MY: 'MYR', TH: 'THB', ID: 'IDR', PH: 'PHP', VN: 'VND',
    KR: 'KRW', TW: 'TWD', LK: 'LKR', NP: 'NPR', BD: 'BDT', PK: 'PKR', MV: 'MVR',
    BR: 'BRL', MX: 'MXN', AR: 'ARS', CL: 'CLP', CO: 'COP', PE: 'PEN', EG: 'EGP',
    NG: 'NGN', KE: 'KES', GH: 'GHS', TZ: 'TZS', MA: 'MAD', MU: 'MUR', FJ: 'FJD'
  };
  EURO.forEach(function (cc) { BY_COUNTRY[cc] = 'EUR'; });

  function currencyFor(cc) {
    if (!cc) return null;
    var code = BY_COUNTRY[cc] || 'USD';
    if (code === STORE) return STORE;
    return rates[code.toLowerCase()] ? code : null;
  }

function init() {
    fetchRates().then(function (r) {
      rates = r;
      var saved = read(KEY_PICK);
      if (saved && (saved === STORE || rates[saved.toLowerCase()])) {
        /* A choice the shopper made themselves always wins over their location. */
        current = saved;
        start();
        return;
      }
      return detectCountry().then(function (cc) {
        var guess = currencyFor(cc);
        if (guess) current = guess;
        start();
      });
    }).catch(function () {
      /* No rates: leave every price in rupees and keep the picker hidden. */
    });
  }

  function start() {
    buildPickers();
    label();
    if (current !== STORE) apply();
    watch();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
